import { getElevationData } from '../../../utils/helpers/HiikerElevationApiHelper';

/** Elevations in metres for [lat, lng] pairs, in order; null when the service fails or answers short. */
export type ElevationFetcher = (latLngs: number[][]) => Promise<number[] | null>;

export const fetchHiikerElevations: ElevationFetcher = async (latLngs) => {
  const rows = await getElevationData(latLngs);
  if (!rows || rows.length !== latLngs.length) return null;
  const values = rows.map((row: any) => (typeof row.elevation === 'string' ? parseFloat(row.elevation) : row.elevation));
  return values.every((v) => typeof v === 'number' && Number.isFinite(v)) ? values : null;
};

export type ElevationResult =
  | { status: 'ok'; key: string; elevations: number[] }
  | { status: 'failed'; key: string }
  | { status: 'stale'; key: string };

/**
 * Loads the profile for the current line. A drag asks for a new profile on every
 * settle, so answers are cached by key and any answer that arrives after a newer
 * request is reported as stale rather than shown.
 */
export class ElevationLoader {
  private cache = new Map<string, number[]>();
  private latest = 0;

  constructor(
    private readonly fetcher: ElevationFetcher = fetchHiikerElevations,
    private readonly maxCached = 24,
  ) {}

  cached(key: string): number[] | null {
    return this.cache.get(key) ?? null;
  }

  async load(key: string, latLngs: number[][]): Promise<ElevationResult> {
    const hit = this.cache.get(key);
    if (hit) {
      this.latest += 1;
      return { status: 'ok', key, elevations: hit };
    }
    const ticket = ++this.latest;
    let elevations: number[] | null = null;
    try {
      elevations = await this.fetcher(latLngs);
    } catch (error) {
      elevations = null;
    }
    if (ticket !== this.latest) return { status: 'stale', key };
    if (!elevations) return { status: 'failed', key };
    this.remember(key, elevations);
    return { status: 'ok', key, elevations };
  }

  private remember(key: string, elevations: number[]) {
    this.cache.set(key, elevations);
    if (this.cache.size > this.maxCached) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) this.cache.delete(oldest);
    }
  }
}

/** A cache key that only changes when the sampled points move by more than ~1 m. */
export function elevationKey(latLngs: number[][]): string {
  return latLngs.map(([lat, lng]) => `${lat.toFixed(5)},${lng.toFixed(5)}`).join(';');
}
